import { useReducedMotion } from "framer-motion";

// ---------------------------------------------------------------------------
// Shared motion language for the Topik player flow (DISTILLED §1.2, A8/A9).
//
// Two clocks: the comparison phase moves with a snappy "doing" tempo; the analysis
// phase settles with a calmer "thinking" tempo, so the deliberate downshift from picking
// to writing is *felt*, not just laid out. Everything here collapses to an instant state
// change under `prefers-reduced-motion` (framer's `useReducedMotion`), which is the
// non-negotiable companion to making motion core - nothing may flash, but nobody who has
// asked their OS for stillness gets motion imposed on them either.
// ---------------------------------------------------------------------------

// The "doing" clock - comparison picks, deal-in, swap.
export const QUICK = { type: "tween", duration: 0.22, ease: [0.4, 0, 0.2, 1] };
// The "thinking" clock - analysis steps settle in slower.
export const CALM = { type: "tween", duration: 0.4, ease: [0.25, 0.1, 0.25, 1] };
// Reduced-motion / instant.
export const INSTANT = { duration: 0 };

// Directional step-swap variants for an AnimatePresence(mode="wait") content region: the
// outgoing step eases out, the incoming eases in. Reduced-motion degrades to a pure
// (positionless, instant) presence swap so the step still changes but never slides.
export const stepVariants = (reduce) => ({
  initial: reduce ? { opacity: 1 } : { opacity: 0, x: 24 },
  animate: { opacity: 1, x: 0 },
  exit: reduce ? { opacity: 1 } : { opacity: 0, x: -24 },
});

// A staggered "deal-in" for a fresh pair of cards (constant, outrunnable, never variable).
export const dealVariants = (reduce) => ({
  initial: reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 },
  animate: (index = 0) => ({
    opacity: 1,
    y: 0,
    transition: reduce ? INSTANT : { ...QUICK, delay: index * 0.08 },
  }),
});

// Hook form: resolves the two clocks + variant sets against the user's motion preference.
export const useTopikMotion = () => {
  const reduce = useReducedMotion();
  return {
    reduce: !!reduce,
    quick: reduce ? INSTANT : QUICK,
    calm: reduce ? INSTANT : CALM,
    step: stepVariants(reduce),
    deal: dealVariants(reduce),
    // Layout transition used by shared-element card travel (pick -> slot/box, finalists ->
    // observation). Same two-clock idea: quick while picking.
    layout: reduce ? INSTANT : QUICK,
  };
};
